import React, { useEffect, useState } from "react";
import moment from "moment/moment";
import { loadApplicantForSummary } from "../../../utils/applicantSummary";
import "../../../styles/document-to-print.css";
import {
  DetailGrid,
  DetailRow,
  DocumentItem,
  PassportPhoto,
  SubCard,
  SummaryHeader,
  SummarySection,
} from "./SummaryLayout";
import "./jamb-summary.css";

const ComponentToPrint = React.forwardRef((props, ref) => {
  const [applicantData, setApplicantData] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    (async function () {
      const summary = await loadApplicantForSummary();

      if (summary) {
        setApplicantData(summary);
        setLoadError("");
      } else {
        setLoadError("Unable to load your application summary. Please contact admissions support.");
      }
    })();
  }, []);

  const passport = applicantData?.documents?.find(
    (value) => value.upload_type === "passport_photo"
  );

  const documents = {
    utme: applicantData?.documents?.find((value) => value.upload_type === "utme_slip"),
    birth: applicantData?.documents?.find((value) => value.upload_type === "birth_cert"),
    nin: applicantData?.documents?.find((value) => value.upload_type === "nin_slip"),
    ssce: applicantData?.documents?.find((value) => value.upload_type === "sse_result"),
    lga: applicantData?.documents?.find((value) => value.upload_type === "lg_origin"),
    bank: applicantData?.documents?.find((value) => value.upload_type === "bank_slip"),
  };

  const fullName = [
    applicantData?.first_name,
    applicantData?.middle_name,
    applicantData?.last_name,
  ].filter(Boolean).join(" ");

  const sessionLabel = applicantData?.session
    ? `${applicantData.session.start_year}/${applicantData.session.end_year}`
    : "";

  const amountPaid = applicantData?.payment?.payment?.amount
    ? applicantData.payment.payment.amount / 100
    : applicantData?.application_fee?.payment_amount;

  const datePaid = applicantData?.payment?.payment?.paid_at
    ? moment(applicantData.payment.payment.paid_at).format("YYYY-MM-DD")
    : applicantData?.payment?.payment?.createdAt
      ? moment(applicantData.payment.payment.createdAt).format("YYYY-MM-DD")
      : applicantData?.application_fee?.createdAt
        ? moment(applicantData.application_fee.createdAt).format("YYYY-MM-DD")
        : "";

  return (
    <div ref={ref} className="jamb-summary-card">
      {loadError && <div className="jamb-summary-alert">{loadError}</div>}

      <SummaryHeader />

      <SummarySection title="Personal Details">
        <div className="jamb-summary-personal">
          <DetailGrid>
            <DetailRow label="First Name" value={applicantData?.first_name} />
            <DetailRow label="Other Name" value={applicantData?.middle_name} />
            <DetailRow label="Last Name" value={applicantData?.last_name} />
            <DetailRow label="Email Address" value={applicantData?.email} />
            <DetailRow label="Phone Number" value={applicantData?.phone} />
            <DetailRow label="Nationality" value={applicantData?.nationality || applicantData?.country} />
            <DetailRow label="State of Origin" value={applicantData?.state} />
            <DetailRow label="L.G.A." value={applicantData?.lg} />
            <DetailRow label="City" value={applicantData?.city} />
            <DetailRow
              label="Date of Birth"
              value={applicantData?.dob ? moment(applicantData.dob).format("YYYY-MM-DD") : ""}
            />
            <DetailRow label="Gender" value={applicantData?.gender} />
            <DetailRow label="Session" value={sessionLabel} />
          </DetailGrid>

          <PassportPhoto
            document={passport}
            alt={fullName ? `${fullName} passport photo` : "Passport photo"}
          />
        </div>
      </SummarySection>

      <SummarySection title="Guardian Information">
        <DetailGrid>
          <DetailRow label="Full Name" value={applicantData?.guardian?.name} />
          <DetailRow label="Relationship" value={applicantData?.guardian?.relationship} />
          <DetailRow label="Phone Number" value={applicantData?.guardian?.phone} />
          <DetailRow label="Address" value={applicantData?.guardian?.address} />
        </DetailGrid>
      </SummarySection>

      <SummarySection title="JAMB Information">
        <DetailGrid>
          <DetailRow label="JAMB Reg. No" value={applicantData?.jamb_number} />
          <DetailRow label="Score" value={applicantData?.jamb_total_score} />
        </DetailGrid>

        {applicantData?.jamb_subject_scores?.length > 0 && (
          <div style={{ marginTop: "1rem" }}>
            <SubCard title="Subjects">
              {applicantData.jamb_subject_scores.map((value, index) => (
                <DetailRow key={index} label={value.subject} value={value.score} />
              ))}
            </SubCard>
          </div>
        )}
      </SummarySection>

      <SummarySection title="Choice Institutions">
        <div className="jamb-summary-two-col">
          <SubCard title="First Choice">
            <DetailRow label="University" value={applicantData?.jamb_first_choice?.university} />
            <DetailRow label="College/Polytechnic" value={applicantData?.jamb_first_choice?.polytechnic} />
            <DetailRow label="Course" value={applicantData?.jamb_first_choice?.course} />
          </SubCard>

          <SubCard title="Second Choice">
            <DetailRow label="University" value={applicantData?.jamb_second_choice?.university} />
            <DetailRow label="College/Polytechnic" value={applicantData?.jamb_second_choice?.polytechnic} />
            <DetailRow label="Course" value={applicantData?.jamb_second_choice?.course} />
          </SubCard>
        </div>
      </SummarySection>

      <SummarySection title="SSCE Result">
        <div className="jamb-summary-two-col">
          <SubCard title="First Sitting">
            <DetailRow label="Type" value={applicantData?.exam_first_sitting?.exam_type} />
            {applicantData?.exam_first_sitting?.subject_grade?.map((value, index) => (
              <DetailRow key={index} label={value?.subject} value={value?.grade} />
            ))}
          </SubCard>

          <SubCard title="Second Sitting">
            <DetailRow label="Type" value={applicantData?.exam_second_sitting?.exam_type} />
            {applicantData?.exam_second_sitting?.subject_grade?.map((value, index) => (
              <DetailRow key={index} label={value?.subject} value={value?.grade} />
            ))}
          </SubCard>
        </div>
      </SummarySection>

      <SummarySection title="Documents / Attachments">
        <div className="jamb-summary-documents">
          <DocumentItem label="UTME Slip" document={documents.utme} />
          <DocumentItem label="Birth Certificate" document={documents.birth} />
          <DocumentItem label="NIN Slip" document={documents.nin} />
          <DocumentItem label="SSCE Certificate" document={documents.ssce} />
          <DocumentItem label="Letter from LGA" document={documents.lga} />
          <DocumentItem label="Proof of Payment" document={documents.bank} />
        </div>
      </SummarySection>

      <SummarySection title="Payment Details">
        <DetailGrid>
          <DetailRow label="Transaction Reference" value={applicantData?.transaction_ref} />
          <DetailRow label="Amount Paid" value={amountPaid ? `₦${Number(amountPaid).toLocaleString()}` : ""} />
          <DetailRow label="Date Paid" value={datePaid} />
        </DetailGrid>
      </SummarySection>

      <SummarySection title="Desired Department and Faculty at Maduka University">
        <div className="jamb-summary-two-col">
          <SubCard title="First Choice">
            <DetailRow label="Faculty" value={applicantData?.applied_first_choice?.faculty} />
            <DetailRow label="Department" value={applicantData?.applied_first_choice?.department} />
          </SubCard>

          <SubCard title="Second Choice">
            <DetailRow label="Faculty" value={applicantData?.applied_second_choice?.faculty} />
            <DetailRow label="Department" value={applicantData?.applied_second_choice?.department} />
          </SubCard>
        </div>
      </SummarySection>
    </div>
  );
});

export default ComponentToPrint;
